import {
  canonDetails,
  canonPalletsFromProduct,
  canonStocksFromProduct,
  normDtype,
  safeArr,
} from './canonical';
import type { PalletRow, ProductFormValues, ProductSnapshot, WarehouseState } from './types';
import { centsToDollarsInput } from '../../addresses/addressForm/money';

/*
 * Turning a saved product back into form state. Kept separate from the
 * canonicalisers because this direction is lossy in the other way: it produces
 * the string-shaped values inputs need, not the comparable ones.
 */

/** Empty form state, used for the create route and when leaving edit mode. */
export const emptyFormValues = (): ProductFormValues => ({
  name: '',
  sku: '',
  description: '',
  deliveryType: 'BULK',
  trackInventory: false,
  defaultLocationId: '',
  whState: {} as WarehouseState,
  palletRows: [] as PalletRow[],
  imageFile: null,
  imagePreview: '',
});

export const emptySnapshot = (): ProductSnapshot => ({
  details: null,
  stocks: null,
  pallets: null,
});

/**
 * Every warehouse gets a row, not just the ones the product sells from —
 * the table lists all of them and an unlisted one could never be switched on.
 */
export const buildWarehouseState = (
  product: any,
  warehouses: Array<{ id: string }>
): WarehouseState => {
  const next: WarehouseState = {};

  for (const s of safeArr(product?.stocks)) {
    next[s.locationId] = {
      enabled: true,
      quantity: String(s.quantity ?? 0),
      stonePrice: centsToDollarsInput(s.stonePriceCents),
    };
  }

  for (const w of warehouses || []) {
    if (!next[w.id]) {
      next[w.id] = {
        enabled: false,
        quantity: '0',
        stonePrice: '',
      };
    }
  }

  return next;
};

export const buildPalletRows = (product: any): PalletRow[] =>
  safeArr(product?.palletSizes).map((ps) => ({
    id: ps.palletTemplateId,
    name: ps.template?.name ?? '',
    type: ps.template?.type ?? '',
    lengthIn: ps.template?.lengthIn ?? '',
    widthIn: ps.template?.widthIn ?? '',
    heightIn: ps.template?.heightIn ?? '',
    weightTons: ps.template?.weightTons ?? '',
  }));

/** The form fields a saved product hydrates directly. */
export const buildFormValuesFromProduct = (
  product: any,
  warehouses: Array<{ id: string }>
): ProductFormValues => ({
  name: product?.name ?? '',
  sku: product?.sku ?? '',
  description: product?.description ?? '',
  deliveryType: normDtype(product?.deliveryType),
  trackInventory: product?.trackInventory !== false,
  defaultLocationId: product?.defaultLocationId || '',
  // Empty preview means no custom photo; the page shows the default image.
  imagePreview: product?.imageUrl || '',
  imageFile: null,
  whState: buildWarehouseState(product, warehouses),
  palletRows: buildPalletRows(product),
});

/**
 * The dirty-check baseline.
 *
 * `defaultLocationId` is included deliberately: leaving it out was the bug that
 * made the page read as dirty the instant it finished loading, because the
 * form's own canonical details always carry it.
 */
export const buildSnapshotFromProduct = (product: any): ProductSnapshot => ({
  details: canonDetails(
    product?.name,
    product?.sku,
    product?.description,
    product?.deliveryType,
    product?.trackInventory,
    product?.defaultLocationId
  ),
  stocks: canonStocksFromProduct(product),
  pallets: canonPalletsFromProduct(product),
});
